import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { SlideOver, SaveStatus } from "./SlideOver";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wrench, Plus, Search, Loader2, Trash2, ArrowRight, User, IndianRupee,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUSES = [
  { key: "open", label: "Open", tone: "amber" },
  { key: "in_progress", label: "In Progress", tone: "sky" },
  { key: "resolved", label: "Resolved", tone: "emerald" },
  { key: "closed", label: "Closed", tone: "slate" },
];

const PRIORITY_TONE = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-sky-100 text-sky-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-rose-100 text-rose-700",
};

const COL_BG = {
  amber: "bg-amber-50/50 border-amber-100",
  sky: "bg-sky-50/50 border-sky-100",
  emerald: "bg-emerald-50/50 border-emerald-100",
  slate: "bg-slate-50 border-slate-100",
};

export function MaintenancePanel({ open, onClose, campusId }) {
  const qc = useQueryClient();
  const [saveStatus, setSaveStatus] = useState("idle");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);

  const listQ = useQuery({
    queryKey: ["infra-maintenance", campusId],
    queryFn: () => api.get(`/infrastructure/maintenance`, { params: { campus_id: campusId } }).then((r) => r.data),
    enabled: !!campusId && open,
  });

  const buildingsQ = useQuery({
    queryKey: ["infra-buildings", campusId],
    queryFn: () => api.get(`/infrastructure/buildings`, { params: { campus_id: campusId } }).then((r) => r.data),
    enabled: !!campusId && open,
  });

  const roomsQ = useQuery({
    queryKey: ["infra-rooms", campusId],
    queryFn: () => api.get(`/infrastructure/rooms`, { params: { campus_id: campusId } }).then((r) => r.data),
    enabled: !!campusId && open,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["infra-maintenance", campusId] });
    qc.invalidateQueries({ queryKey: ["infra-dashboard", campusId] });
  };

  const wrap = async (fn) => {
    setSaveStatus("saving");
    try {
      await fn();
      setSaveStatus("saved");
      invalidate();
      setTimeout(() => setSaveStatus("idle"), 1200);
    } catch (err) {
      setSaveStatus("error");
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
  };

  const tickets = useMemo(() => {
    const raw = (listQ.data || []).map((t) => ({
      ...t,
      status: t.config?.status || t.status || "open",
    }));
    const s = q.trim().toLowerCase();
    return s
      ? raw.filter((t) => (t.name || "").toLowerCase().includes(s) || (t.ticket_no || "").toLowerCase().includes(s))
      : raw;
  }, [listQ.data, q]);

  const byStatus = useMemo(() => {
    const m = { open: [], in_progress: [], resolved: [], closed: [] };
    for (const t of tickets) (m[t.status] || m.open).push(t);
    return m;
  }, [tickets]);

  const updateStatus = (t, next) =>
    wrap(() => api.patch(`/infrastructure/maintenance/${t.id}`, {
      config: { ...(t.config || {}), status: next, ...(next === "resolved" ? { resolved_at: new Date().toISOString() } : {}) },
    }));

  const removeTicket = (t) => wrap(() => api.delete(`/infrastructure/maintenance/${t.id}`));

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Maintenance"
      subtitle={`${tickets.length} ticket${tickets.length === 1 ? "" : "s"} in this workflow.`}
      right={<SaveStatus status={saveStatus} />}
      width="max-w-6xl"
      testid="maintenance"
    >
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by ticket number or title…"
              className="pl-9"
              data-testid="maint-search"
            />
          </div>
          <Button onClick={() => setShowNew(true)} data-testid="new-ticket-btn">
            <Plus className="h-4 w-4 mr-1" /> New Ticket
          </Button>
        </div>

        {listQ.isLoading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {STATUSES.map((col) => (
              <StatusColumn
                key={col.key}
                col={col}
                tickets={byStatus[col.key] || []}
                onAdvance={(t) => {
                  const idx = STATUSES.findIndex((s) => s.key === col.key);
                  const nextCol = STATUSES[idx + 1];
                  if (nextCol) updateStatus(t, nextCol.key);
                }}
                onRemove={removeTicket}
              />
            ))}
          </div>
        )}
      </div>

      {showNew && (
        <NewTicketModal
          campusId={campusId}
          buildings={buildingsQ.data || []}
          rooms={roomsQ.data || []}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            invalidate();
          }}
          setStatus={setSaveStatus}
        />
      )}
    </SlideOver>
  );
}

function StatusColumn({ col, tickets, onAdvance, onRemove }) {
  return (
    <div className={cn("rounded-xl border p-3", COL_BG[col.tone] || "bg-white border-slate-200")}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">{col.label}</div>
        <div className="text-xs font-mono px-2 py-0.5 rounded-full bg-white/60 text-slate-500">{tickets.length}</div>
      </div>
      <ul className="space-y-2" data-testid={`maint-col-${col.key}`}>
        {tickets.length === 0 && (
          <li className="text-[11px] text-slate-400 italic px-1 py-2">Empty</li>
        )}
        {tickets.map((t) => (
          <li key={t.id} className="rounded-lg bg-white border border-slate-200 p-3 space-y-1.5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">{t.ticket_no}</span>
              {t.config?.priority && (
                <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase", PRIORITY_TONE[t.config.priority] || PRIORITY_TONE.low)}>
                  {t.config.priority}
                </span>
              )}
            </div>
            <div className="text-sm font-medium text-slate-900 truncate">{t.name}</div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
              {t.config?.assignee && (<span className="inline-flex items-center gap-0.5"><User className="h-3 w-3" /> {t.config.assignee}</span>)}
              {t.config?.cost != null && (<span className="inline-flex items-center gap-0.5"><IndianRupee className="h-3 w-3" /> {t.config.cost}</span>)}
              {t.config?.location && (<span className="truncate">{t.config.location}</span>)}
            </div>
            <div className="flex items-center justify-end gap-1 pt-1">
              {col.key !== "closed" && (
                <button
                  type="button"
                  onClick={() => onAdvance(t)}
                  className="text-[11px] px-2 py-1 rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1"
                  data-testid={`ticket-advance-${t.id}`}
                >
                  Advance <ArrowRight className="h-3 w-3" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onRemove(t)}
                className="h-7 w-7 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center"
                data-testid={`ticket-del-${t.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewTicketModal({ campusId, buildings, rooms, onClose, onCreated, setStatus }) {
  const [form, setForm] = useState({
    name: "",
    priority: "medium",
    building_id: "",
    room_id: "",
    assignee: "",
    vendor: "",
    cost: "",
    remarks: "",
  });

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Give the ticket a title");
      return;
    }
    const roomLabel = rooms.find((r) => r.id === form.room_id)?.name;
    const bldLabel = buildings.find((b) => b.id === form.building_id)?.name;
    setStatus("saving");
    try {
      await api.post(`/infrastructure/maintenance`, {
        campus_id: campusId,
        kind: "ticket",
        name: form.name.trim(),
        config: {
          status: "open",
          priority: form.priority,
          building_id: form.building_id || null,
          room_id: form.room_id || null,
          location: [bldLabel, roomLabel].filter(Boolean).join(" · "),
          assignee: form.assignee.trim(),
          vendor: form.vendor.trim(),
          cost: form.cost ? Number(form.cost) : null,
          remarks: form.remarks,
          created_at: new Date().toISOString(),
        },
      });
      setStatus("saved");
      toast.success("Ticket created");
      onCreated();
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Could not create");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-ticket-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">New Maintenance Ticket</h3>
          <p className="text-sm text-slate-500">Ticket number is auto-generated.</p>
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Title *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. AC not cooling in Room 204"
            data-testid="ticket-title"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Assignee</Label>
            <Input
              value={form.assignee}
              onChange={(e) => setForm((f) => ({ ...f, assignee: e.target.value }))}
              placeholder="Name / vendor"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Building</Label>
            <Select value={form.building_id} onValueChange={(v) => setForm((f) => ({ ...f, building_id: v, room_id: "" }))}>
              <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Room</Label>
            <Select value={form.room_id} onValueChange={(v) => setForm((f) => ({ ...f, room_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {rooms
                  .filter((r) => !form.building_id || r.building_id === form.building_id)
                  .map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Vendor</Label>
            <Input value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Cost (₹)</Label>
            <Input type="number" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Remarks</Label>
          <Textarea
            rows={2}
            value={form.remarks}
            onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="ticket-create-save">Create ticket</Button>
        </div>
      </div>
    </div>
  );
}
